// pages/talent/PostEventSurveyV2.js — Iter-133s
// Dynamic 10-section post-event survey for marketing/activation roles.
// Renders whatever /api/survey-v2/schema returns so we don't have to
// ship the form in code when a question set evolves.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';
import { enqueueUpload, isNetworkError } from '../../utils/offlineUploadQueue';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, CheckCircle2, Camera, X, Save, Send, Upload } from 'lucide-react';


const YesNo = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-md overflow-hidden border border-gray-300" data-testid={testid}>
    {['yes', 'no'].map((v) => (
      <button
        type="button"
        key={v}
        onClick={() => onChange(v)}
        className={`px-3 py-1.5 text-sm capitalize ${
          value === v
            ? (v === 'yes' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {v}
      </button>
    ))}
  </div>
);


const StarRating = ({ value, onChange, testid }) => (
  <div className="inline-flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-8 h-8 rounded-full text-sm font-semibold ${
          Number(value) >= n ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-400 hover:bg-amber-100'
        }`}
      >{n}</button>
    ))}
  </div>
);


export default function PostEventSurveyV2() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [autofill, setAutofill] = useState(null);
  const [responses, setResponses] = useState({});
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ required: false, classification: 'default_required', role_name: '' });
  // Iter-260 — live photo-requirement counter
  const [photoReq, setPhotoReq] = useState({ min_photos: 4, current_count: 0, satisfied: false });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [schemaRes, bookingRes, photoReqRes] = await Promise.all([
          api.get('/survey-v2/schema'),
          api.get(`/survey-v2/booking/${bookingId}`),
          api.get(`/recap-photo-requirements/for-booking/${bookingId}`).catch(() => ({ data: null })),
        ]);
        setSchema(schemaRes.data.sections);
        setAutofill(bookingRes.data.autofill || null);
        setMeta({
          required: bookingRes.data.survey_required,
          classification: bookingRes.data.role_classification,
          role_name: bookingRes.data.role_name,
        });
        if (photoReqRes.data) setPhotoReq(photoReqRes.data);
        if (bookingRes.data.survey) {
          setSurvey(bookingRes.data.survey);
          setResponses(bookingRes.data.survey.responses || {});
          setMedia(bookingRes.data.survey.media || []);
        }
      } catch (err) {
        toast.error('Failed to load survey: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId]);

  const setAnswer = (sectionId, key, value) => {
    setResponses((prev) => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/survey-v2/booking/${bookingId}/save`, { responses });
      toast.success('Draft saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const submit = async () => {
    // Iter-133t — client-side required-field enforcement before submit.
    // Server also enforces media-min-1 for activation roles, but we gate
    // on the client first to give crisp inline feedback.
    if (schema) {
      for (const section of schema) {
        for (const q of section.questions) {
          const val = (responses[section.id] || {})[q.key];
          const missing = val === undefined || val === '' || val === null;

          if (q.required && missing) {
            toast.error(`Please answer: ${q.label}`);
            return;
          }
          if (q.required_if_shown && q.show_if) {
            const [ck, cv] = Object.entries(q.show_if)[0];
            if ((responses[section.id] || {})[ck] === cv && missing) {
              toast.error(`Please answer: ${q.label}`);
              return;
            }
          }
        }
        // Activation-role media-min enforcement.
        if (section.id === 'J' && meta.classification === 'marketing_activation' && (section.min_media || 0) > 0) {
          if (media.length < section.min_media) {
            toast.error(`At least ${section.min_media} photo${section.min_media === 1 ? '' : 's'} required before submitting.`);
            return;
          }
        }
      }
    }

    setSubmitting(true);
    try {
      await api.post(`/survey-v2/booking/${bookingId}/submit`, { responses });
      toast.success('Survey submitted. Thank you!');
      navigate('/talent/home');
    } catch (e) {
      toast.error('Submit failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const uploadPhoto = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    let queued = 0;
    for (let f of Array.from(files)) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); continue; }
      f = await downscaleImage(f); // mobile camera photos are 6-15MB raw
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: >10MB`); continue; }
      try {
        const fd = new FormData(); fd.append('file', f);
        const res = await api.post(`/survey-v2/booking/${bookingId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        added.push(res.data);
      } catch (e) {
        if (isNetworkError(e)) {
          await enqueueUpload({
            endpoint: `/survey-v2/booking/${bookingId}/upload`,
            file: f,
            description: 'recap photo',
          });
          queued += 1;
        } else {
          toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
        }
      }
    }
    if (queued > 0) {
      toast.info(`No signal — ${queued} photo${queued > 1 ? 's' : ''} saved on this device`, {
        description: 'They will upload automatically when you are back online and count toward your recap.',
      });
    }
    if (added.length) {
      const newMedia = [...media, ...added];
      setMedia(newMedia);
      // Iter-260 — keep the live counter in sync as photos are added.
      setPhotoReq((prev) => ({
        ...prev,
        current_count: newMedia.length,
        satisfied: newMedia.length >= (prev.min_photos || 4),
      }));
    }
    setUploading(false);
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/survey-v2/media/${id}`);
      const newMedia = media.filter((m) => m.id !== id);
      setMedia(newMedia);
      // Iter-260 — keep counter in sync when photos are removed.
      setPhotoReq((prev) => ({
        ...prev,
        current_count: newMedia.length,
        satisfied: newMedia.length >= (prev.min_photos || 4),
      }));
    } catch (e) { toast.error('Delete failed'); }
  };

  const renderQuestion = (section, q) => {
    const val = responses[section.id]?.[q.key];
    const tid = `survey-q-${section.id}-${q.key}`;
    // show_if gate
    if (q.show_if) {
      const [ck, cv] = Object.entries(q.show_if)[0];
      if (responses[section.id]?.[ck] !== cv) return null;
    }
    // Mark required questions with an asterisk in the label.
    const isRequired = q.required || (q.required_if_shown && q.show_if);
    const label = isRequired ? <>{q.label} <span className="text-red-500">*</span></> : q.label;
    switch (q.type) {
      case 'readonly':
        return (
          <div key={q.key} className="flex items-center justify-between py-1.5">
            <span className="text-sm text-gray-600">{q.label}</span>
            <span className="text-sm font-medium text-gray-900" data-testid={tid}>
              {autofill?.[q.key] || '—'}
            </span>
          </div>
        );
      case 'text':
        return (
          <div key={q.key} className="space-y-1">
            <Label className="text-sm text-gray-700">{label}</Label>
            <Input value={val || ''} onChange={(e) => setAnswer(section.id, q.key, e.target.value)} data-testid={tid} />
          </div>
        );
      case 'textarea':
        return (
          <div key={q.key} className="space-y-1">
            <Label className="text-sm text-gray-700">{label}</Label>
            <Textarea rows={3} value={val || ''} onChange={(e) => setAnswer(section.id, q.key, e.target.value)} data-testid={tid} />
          </div>
        );
      case 'number':
        return (
          <div key={q.key} className="space-y-1">
            <Label className="text-sm text-gray-700">{label}</Label>
            <Input type="number" inputMode="numeric" value={val ?? ''} onChange={(e) => setAnswer(section.id, q.key, e.target.value)} data-testid={tid} />
          </div>
        );
      case 'yes_no':
        return (
          <div key={q.key} className="flex items-center justify-between py-1.5 gap-4">
            <Label className="text-sm text-gray-700 flex-1">{label}</Label>
            <YesNo value={val} onChange={(v) => setAnswer(section.id, q.key, v)} testid={tid} />
          </div>
        );
      case 'select':
        return (
          <div key={q.key} className="space-y-1">
            <Label className="text-sm text-gray-700">{label}</Label>
            <select
              className="w-full rounded-md border border-gray-300 bg-white text-sm px-3 py-2"
              value={val || ''}
              onChange={(e) => setAnswer(section.id, q.key, e.target.value)}
              data-testid={tid}
            >
              <option value="">Select…</option>
              {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        );
      case 'rating_1_5':
        return (
          <div key={q.key} className="flex items-center justify-between py-1.5 gap-4">
            <Label className="text-sm text-gray-700 flex-1">{label}</Label>
            <StarRating value={val} onChange={(n) => setAnswer(section.id, q.key, n)} testid={tid} />
          </div>
        );
      case 'media_uploader':
        // Iter-260 — live counter (X / N), dual camera-vs-gallery buttons,
        // colour swap when minimum is satisfied.
        const min = photoReq.min_photos || 4;
        const cnt = media.length;
        const ok = cnt >= min;
        return (
          <div key={q.key} className="space-y-2" data-testid={tid}>
            <p className="text-xs text-gray-600">{q.label}</p>
            <div className={`flex items-center gap-2 text-xs font-semibold ${ok ? 'text-emerald-700' : 'text-amber-700'}`}
                 data-testid="recap-photo-counter">
              <span className={`px-2 py-1 rounded ${ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                {cnt} / {min} photos uploaded {ok ? '✓' : ''}
              </span>
              {!ok && <span className="font-normal text-gray-600">Minimum required to submit</span>}
            </div>
            {q.note && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{q.note}</p>}
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-[#03DFFC] px-4 py-2 text-sm text-white font-medium" data-testid="survey-camera-photo-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Take photo
                <input type="file" accept="image/*" capture="environment" className="hidden"
                       onChange={(e) => { uploadPhoto(e.target.files); e.target.value = ''; }} />
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-white border border-gray-300 px-4 py-2 text-sm text-gray-700 font-medium hover:bg-gray-50" data-testid="survey-upload-photo-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Choose from gallery
                <input type="file" accept="image/*" multiple className="hidden"
                       onChange={(e) => { uploadPhoto(e.target.files); e.target.value = ''; }} />
              </label>
            </div>
            {media.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 pt-2">
                {media.map((m) => (
                  <div key={m.id} className="relative group">
                    <img src={m.file_url} alt={m.caption || 'photo'} className="w-full h-24 object-cover rounded-md border" />
                    <button type="button" onClick={() => removePhoto(m.id)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100"
                            data-testid={`recap-photo-remove-${m.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const completedCount = useMemo(() => {
    if (!schema) return 0;
    let done = 0;
    schema.forEach((s) => {
      const sr = responses[s.id] || {};
      s.questions.forEach((q) => {
        if (q.type === 'readonly' || q.type === 'media_uploader') return;
        if (q.show_if) {
          const [ck, cv] = Object.entries(q.show_if)[0];
          if ((responses[s.id] || {})[ck] !== cv) return;
        }
        if (sr[q.key] !== undefined && sr[q.key] !== '' && sr[q.key] !== null) done += 1;
      });
    });
    return done;
  }, [responses, schema]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!meta.required) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 max-w-xl mx-auto">
        <Card>
          <CardHeader><CardTitle>Post-event survey not required</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">
              Based on the role ({meta.role_name || 'your role'}), no post-event survey is required.
              Your payout will proceed normally once all other checks pass.
            </p>
            <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const alreadySubmitted = !!survey?.submitted_at;

  return (
    <div className="min-h-screen bg-gray-50 pb-28" data-testid="post-event-survey-v2">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-600 text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <div className="text-sm font-semibold text-gray-900">Post-Event Report</div>
          <Badge variant="outline" className="text-xs">{completedCount} answered</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-5" data-testid="survey-form">
        <Card className="bg-gradient-to-br from-[#03DFFC]/5 to-white border-[#03DFFC]/30">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-[#03DFFC] font-semibold">
              Post-Event Report — {meta.role_name || 'Activation role'}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              This survey is required for marketing / brand-activation roles before your payout is released.
            </p>
            {alreadySubmitted && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Submitted on {new Date(survey.submitted_at).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>

        {schema.map((section) => (
          <Card key={section.id} data-testid={`survey-section-${section.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-bold">{section.id}</span>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {section.questions.map((q) => renderQuestion(section, q))}
            </CardContent>
          </Card>
        ))}
      </main>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t p-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3 justify-end">
          {/* Iter-260 — block submit until the activation photo minimum is met */}
          {meta.classification === 'marketing_activation' && photoReq.min_photos > 0 && !photoReq.satisfied && !alreadySubmitted && (
            <div className="text-xs text-amber-700 font-medium mr-auto" data-testid="recap-photo-gate-hint">
              {photoReq.current_count}/{photoReq.min_photos} photos uploaded — submit unlocks at {photoReq.min_photos}.
            </div>
          )}
          <Button variant="outline" onClick={save} disabled={saving || alreadySubmitted} data-testid="survey-save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save draft
          </Button>
          <Button
            onClick={submit}
            disabled={
              submitting || alreadySubmitted ||
              (meta.classification === 'marketing_activation' && !photoReq.satisfied)
            }
            data-testid="survey-submit-btn"
            className="bg-[#03DFFC] hover:bg-[#00C3DE] text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {alreadySubmitted ? 'Submitted' : 'Submit recap'}
          </Button>
        </div>
      </div>
    </div>
  );
}
